import { AppendReason, RemoveReason, ViewCompute, ViewUpdate, VObjectInListGeneric } from "@1oo4/miv";
import { VText } from "./contents";
import { MRichArticleReference, MRichChapterReference, MRichLineBreak, MRichLink, MRichReference, MRichSectionReference, MRichSpan, MRichTextItem } from "../../shared/model/richtext";
import { DocPage, VRootDoc } from ".";

export abstract class VRichTextItem extends VObjectInListGeneric {
    private readonly para: HTMLParagraphElement;

    constructor(m: MRichTextItem, para: HTMLParagraphElement) {
        super(m);
        this.para = para;
    }

    abstract getView(): HTMLElement;

    protected AppendToGUI(ar: AppendReason, vu: ViewUpdate): void {
        this.para.appendChild(this.getView());
    }
    protected InsertIntoGUI(index: number, vu: ViewUpdate): void {
        this.para.insertBefore(this.getView(), this.para.children[index]);
    }
    protected RemoveFromGUI(rr: RemoveReason, vu: ViewUpdate): void {
        this.para.removeChild(this.getView());
    }
}

export class VLineBreak extends VRichTextItem {
    private readonly view: HTMLBRElement;
    constructor(m: MRichLineBreak, para: HTMLParagraphElement) {
        super(m, para);
        this.view = document.createElement("br");
    }
    getView(): HTMLElement {
        return this.view;
    }
}

export class VSpan extends VRichTextItem {
    private readonly view: HTMLSpanElement;
    private readonly mSpan: MRichSpan;

    constructor(m: MRichSpan, para: HTMLParagraphElement) {
        super(m, para);
        this.mSpan = m;
        this.view = document.createElement("span");
        this.view.classList.add("rich_normal");
    }
    getView(): HTMLElement {
        return this.view;
    }

    protected ModelUpdated(vu: ViewUpdate): void {
        if (vu.IsChanged(this.mSpan.Text)) {
            this.view.textContent = this.mSpan.Text.Value;
        }
        if (vu.IsChanged(this.mSpan.Style)) {
            while (this.view.classList.length > 1) {
                this.view.classList.remove(this.view.classList[this.view.classList.length - 1]);
            }
            if (this.mSpan.Style.IsBold) this.view.classList.add("rich_bold");
            if (this.mSpan.Style.IsUnderline) this.view.classList.add("rich_underline");
            if (this.mSpan.Style.IsItalic) this.view.classList.add("rich_italic");
            if (this.mSpan.Style.IsCode) this.view.classList.add("rich_code");
        }
    }
}

export class VLink extends VRichTextItem {
    private readonly view: HTMLAnchorElement;
    private readonly mSpan: MRichLink;

    constructor(m: MRichLink, para: HTMLParagraphElement) {
        super(m, para);
        this.mSpan = m;
        this.view = document.createElement("a");
        this.view.innerText = m.DisplayName;
        this.view.href = m.HRef;
        this.view.classList.add("rich_link");
    }
    getView(): HTMLElement {
        return this.view;
    }
}

abstract class VReference extends VRichTextItem {

    constructor(m: MRichReference, para: HTMLParagraphElement) {
        super(m, para);
    }

    abstract getRef(): MRichReference;
    abstract getView(): HTMLSpanElement;

    protected ModelUpdated(vu: ViewUpdate): void {
        const name = this.getRef().Name;
        if (vu.IsChanged(name)) {
            this.getView().textContent = name.Value;
        }
        this.RefUpdated(vu);
    }
    protected abstract RefUpdated(vu: ViewCompute): void;
}

export class VRichChapter extends VReference {
    private readonly cRef: MRichChapterReference;
    private readonly span: HTMLSpanElement;

    constructor(m: MRichChapterReference, para: HTMLParagraphElement, root: VRootDoc) {
        super(m, para);
        this.cRef = m;
        this.span = document.createElement("span");
        this.span.classList.add("rich_ref");
        this.span.classList.add("rich_ref_chapter");
        this.span.onclick = () => {
            for (let i = 0; i < root.chapters.Count; i++) {
                const chap = root.chapters.Get(i);
                if (chap.Model == this.cRef.Chapter) {
                    chap.bringIntoView(true);
                    return;
                }
            }
        }
    }

    getRef(): MRichReference {
        return this.cRef;
    }
    getView(): HTMLSpanElement {
        return this.span;
    }

    protected RefUpdated(vu: ViewCompute): void {
        // rien
    }
}

abstract class VRichLink extends VReference {
    protected readonly link: HTMLAnchorElement;

    constructor(m: MRichReference, para: HTMLParagraphElement) {
        super(m, para);
        this.link = document.createElement("a");
        this.link.classList.add("rich_ref");
    }

    getView(): HTMLSpanElement {
        return this.link;
    }
}

export class VRichArticle extends VRichLink {
    private readonly aRef: MRichArticleReference;
    constructor(m: MRichArticleReference, para: HTMLParagraphElement) {
        super(m, para);
        this.aRef = m;
        this.link.classList.add("rich_ref_article");
    }
    getRef(): MRichReference {
        return this.aRef;
    }
    protected RefUpdated(vu: ViewCompute): void {
        if (vu.IsChanged(this.aRef.Name)) {
            this.link.href = DocPage.getArticleURL(this.aRef.Article);
        }
    }
}

export class VRichSection extends VRichLink {
    private readonly sRef: MRichSectionReference;
    constructor(m: MRichSectionReference, para: HTMLParagraphElement) {
        super(m, para);
        this.sRef = m;
        this.link.classList.add("rich_ref_section");
    }
    getRef(): MRichReference {
        return this.sRef;
    }
    protected RefUpdated(vu: ViewCompute): void {
        if (vu.IsChanged(this.sRef.Name)) {
            this.link.href = DocPage.getSectionURL(this.sRef.Section);
        }
    }
}